'use client';

import { usePathname } from 'next/navigation';
import Script from 'next/script';
import React, { useEffect, useRef } from 'react';

import { ANALYSIS_COOKIES_PROPERTY_NAME } from '@cineamo/ui/src/constants/cookie-constants';

import useCookieConsent from '@cineamo/ui/src/hooks/useCookieConsent';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

declare global {
    interface Window {
        fbq?: ((...args: unknown[]) => void) & {
            callMethod?: (...args: unknown[]) => void;
            queue?: unknown[];
            loaded?: boolean;
            version?: string;
        };
        _fbq?: Window['fbq'];
    }
}

const META_PIXEL_NOSCRIPT_URL = 'https://www.facebook.com/tr';

/**
 * Loads the Meta Pixel base code (fbq init + PageView) when a Meta Pixel ID is configured for the
 * cinema and the visitor has consented to analysis cookies. Fires an additional PageView on
 * client-side route changes, since the SPA navigation after the initial load isn't a full page load.
 */
export default function MetaPixel(): React.JSX.Element | null {
    const domainConfiguration = useDomainConfiguration();
    // The ID is interpolated into an inline script, and the API stores `websiteConfiguration` unvalidated,
    // so anything but digits is refused here, the same as MatomoTracker does with its site ID.
    const configuredPixelId = domainConfiguration.websiteConfiguration?.metaPixelId?.trim();
    const metaPixelId = configuredPixelId && /^\d+$/.test(configuredPixelId) ? configuredPixelId : null;

    // `useCookieConsent` reads the cookie in an effect and starts out denied, so the pixel is never
    // rendered on the server or in the hydrating client render — only once a stored `true` is seen.
    const hasAnalysisConsent = useCookieConsent(ANALYSIS_COOKIES_PROPERTY_NAME);

    const isEnabled = !!metaPixelId && hasAnalysisConsent;

    const pathname = usePathname();
    const isInitialPageviewRef = useRef(true);

    useEffect(() => {
        if (!isEnabled || typeof window === 'undefined' || !window.fbq) {
            return;
        }

        // The base pixel snippet below already fires the first PageView on load.
        if (isInitialPageviewRef.current) {
            isInitialPageviewRef.current = false;
            return;
        }

        window.fbq('track', 'PageView');
    }, [isEnabled, pathname]);

    if (!isEnabled) {
        return null;
    }

    const initScript = `
        !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
        n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${metaPixelId}');
        fbq('track', 'PageView');
    `;

    return (
        <>
            <Script
                id="meta-pixel-base"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{ __html: initScript }}
            />
            <noscript>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                    height="1"
                    width="1"
                    alt=""
                    style={{ display: 'none' }}
                    src={`${META_PIXEL_NOSCRIPT_URL}?id=${metaPixelId}&ev=PageView&noscript=1`}
                />
            </noscript>
        </>
    );
}
